import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, ArrowRight, Loader2 } from 'lucide-react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [authMethod, setAuthMethod] = useState('phone');
  const [step, setStep] = useState('input');
  const [loading, setLoading] = useState(false);

  const [identifier, setIdentifier] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [requiresPhoneVerification, setRequiresPhoneVerification] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let endpoint = authMethod === 'phone' ? '/auth/phone/send-otp' : '/auth/email/send-otp';
      let payload = authMethod === 'phone' ? { phone: identifier } : { email: identifier };

      const response = await api.post(endpoint, payload);
      
      if (response.data.requires_phone_verification) {
        setRequiresPhoneVerification(true);
      }
      
      setStep('otp');
      toast.success('OTP sent! Check your ' + (authMethod === 'phone' ? 'SMS' : 'email'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = authMethod === 'phone' ? '/auth/phone/verify-otp' : '/auth/email/verify-otp';
      const response = await api.post(endpoint, {
        identifier: identifier,
        otp_code: otp,
        name: name || 'User'
      });

      const { session_token, user, message } = response.data;

      if (!session_token && message.includes('phone')) {
        setStep('phone-verify');
        setAuthMethod('phone');
        setIdentifier('');
        setOtp('');
        toast.info('Business email detected. Please verify your phone.');
        return;
      }

      await login(session_token, user);
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    toast.info('Google OAuth integration coming soon');
  };

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center bg-brand-timberwolf" data-testid="login-page">
      <div className="container-custom py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-md mx-auto"
        >
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center space-x-3">
              <div className="w-12 h-12 rounded-sm border-2 border-brand-pearl flex items-center justify-center">
                <span className="text-2xl font-heading font-bold text-brand-pearl">R</span>
              </div>
              <span className="text-2xl font-heading font-semibold text-brand-pearl">Rudra Apparel</span>
            </Link>
          </div>

          <div className="bg-brand-ivory rounded-sm border border-brand-pearl/10 p-8">
            <h1 className="text-3xl font-heading font-bold text-brand-pearl mb-2" data-testid="login-heading">
              {step === 'phone-verify' ? 'Verify Phone' : 'Welcome Back'}
            </h1>
            <p className="text-brand-walnut mb-6">
              {step === 'input' && 'Sign in or create account'}
              {step === 'otp' && `OTP sent to your ${authMethod}`}
              {step === 'phone-verify' && 'Verify phone for business email'}
            </p>

            {step === 'input' && (
              <>
                <div className="flex gap-2 mb-6">
                  <button
                    onClick={() => setAuthMethod('phone')}
                    data-testid="auth-method-phone"
                    className={`flex-1 py-2 px-4 rounded-sm text-sm font-medium ${authMethod === 'phone' ? 'bg-brand-pearl text-brand-ivory' : 'bg-brand-timberwolf text-brand-walnut'}`}
                  >
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone
                  </button>
                  <button
                    onClick={() => setAuthMethod('email')}
                    data-testid="auth-method-email"
                    className={`flex-1 py-2 px-4 rounded-sm text-sm font-medium ${authMethod === 'email' ? 'bg-brand-pearl text-brand-ivory' : 'bg-brand-timberwolf text-brand-walnut'}`}
                  >
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email
                  </button>
                </div>

                <form onSubmit={handleSendOTP} data-testid="auth-input-form">
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-brand-pearl mb-2">
                      {authMethod === 'phone' ? 'Phone Number' : 'Email'}
                    </label>
                    <input
                      type={authMethod === 'phone' ? 'tel' : 'email'}
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      placeholder={authMethod === 'phone' ? '+91 98765 43210' : 'you@example.com'}
                      data-testid="auth-identifier-input"
                      className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="send-otp-btn"
                    className="w-full flex items-center justify-center px-6 py-3 bg-brand-pearl text-brand-ivory font-medium rounded-sm hover:bg-brand-walnut transition-colors disabled:opacity-50"
                  >
                    {loading ? <><Loader2 className="mr-2 w-5 h-5 animate-spin" />Sending...</> : <>Send OTP<ArrowRight className="ml-2 w-5 h-5" /></>}
                  </button>
                </form>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-brand-pearl/10" /></div>
                  <div className="relative flex justify-center text-sm"><span className="px-2 bg-brand-ivory text-brand-walnut">Or continue with</span></div>
                </div>

                <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                  <div className="flex justify-center" data-testid="google-login-btn">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => toast.error('Google auth failed')}
                      theme="outline"
                      size="large"
                    />
                  </div>
                </GoogleOAuthProvider>
              </>
            )}

            {(step === 'otp' || step === 'phone-verify') && (
              <form onSubmit={handleVerifyOTP} data-testid="auth-otp-form">
                {step === 'phone-verify' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-brand-pearl mb-2">Phone</label>
                    <input
                      type="tel"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                      required
                    />
                    <button type="button" onClick={handleSendOTP} className="mt-2 text-sm text-brand-pearl">Send OTP</button>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-brand-pearl mb-2">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your Name"
                    className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-brand-pearl mb-2">OTP Code</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength="6"
                    data-testid="otp-input"
                    className="w-full px-4 py-3 border border-brand-pearl/20 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-pearl bg-white text-center text-2xl tracking-widest font-mono"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  data-testid="verify-otp-btn"
                  className="w-full flex items-center justify-center px-6 py-3 bg-brand-pearl text-brand-ivory font-medium rounded-sm hover:bg-brand-walnut transition-colors disabled:opacity-50"
                >
                  {loading ? <><Loader2 className="mr-2 w-5 h-5 animate-spin" />Verifying...</> : <>Verify<ArrowRight className="ml-2 w-5 h-5" /></>}
                </button>

                <button type="button" onClick={() => { setStep('input'); setOtp(''); }} className="w-full mt-3 text-sm text-brand-walnut">Back</button>
              </form>
            )}

            <p className="mt-6 text-xs text-center text-brand-walnut">
              By continuing, you agree to our <Link to="/terms" className="text-brand-pearl">Terms</Link> and <Link to="/privacy" className="text-brand-pearl">Privacy Policy</Link>
            </p>
          </div>

          <div className="text-center mt-6">
            <Link to="/" className="text-brand-walnut hover:text-brand-pearl text-sm">← Back to Home</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;